import React from 'react';
import { useI18n } from '../../../contexts/I18nContext';
import { UserDetailState } from './useUserDetail';

/** The Details tab: identity and account status, read or edited in place. */
export const UserDetailsTab: React.FC<{ state: UserDetailState }> = ({ state }) => {
  const { formatDateTime, t } = useI18n();
  const { user, isEditing, isLoading, showsInternals } = state;
  if (!user) return null;

  return (
    <div className="tab-content ud-tab">
      <section className="cu-section">
        <header className="cu-section-head">
          <h3>{t('users.detail.identity')}</h3>
          <p>{t('users.detail.identityHint')}</p>
        </header>
        <div className="cu-grid">
          <div className="cu-field">
            <label htmlFor="ud-first">{t('users.firstName')}</label>
            {isEditing ? (
              <input id="ud-first" type="text" className="cu-input" data-testid="field-user-first-name-edit" value={state.editFirstName}
                     onChange={(e) => state.setEditFirstName(e.target.value)} disabled={isLoading} autoFocus maxLength={100} />
            ) : (
              <span className="ud-value">{user.firstName || '–'}</span>
            )}
          </div>
          <div className="cu-field">
            <label htmlFor="ud-last">{t('users.lastName')}</label>
            {isEditing ? (
              <input id="ud-last" type="text" className="cu-input" data-testid="field-user-last-name-edit" value={state.editLastName}
                     onChange={(e) => state.setEditLastName(e.target.value)} disabled={isLoading} maxLength={100} />
            ) : (
              <span className="ud-value">{user.lastName || '–'}</span>
            )}
          </div>
          <div className="cu-field">
            <label htmlFor="ud-email">{t('users.email')}</label>
            {isEditing ? (
              <input id="ud-email" type="email" className="cu-input" data-testid="field-user-email-edit" value={state.editEmail}
                     onChange={(e) => state.setEditEmail(e.target.value)} disabled={isLoading} />
            ) : (
              <span className="ud-value">{user.email || '–'}</span>
            )}
          </div>
          <div className="cu-field">
            <label>{t('users.username')}</label>
            <span className="ud-value mono">{user.username}</span>
          </div>
          {showsInternals && (
            <div className="cu-field cu-field-wide">
              <label>{t('users.detail.keycloakId')}</label>
              <span className="ud-value mono ud-value-small">{user.id}</span>
            </div>
          )}
        </div>
      </section>

      <section className="cu-section">
        <header className="cu-section-head">
          <h3>{t('users.detail.account')}</h3>
          <p>{t('users.accountStatusHint')}</p>
        </header>
        <div className="cu-grid cu-grid-3">
          <div className="cu-field">
            <label htmlFor="ud-enabled">{t('users.accountStatus')}</label>
            {isEditing ? (
              <select id="ud-enabled" className="cu-input" data-testid="field-user-enabled-edit" value={state.editEnabled ? 'active' : 'inactive'}
                      onChange={(e) => state.setEditEnabled(e.target.value === 'active')} disabled={isLoading}>
                <option value="active">{t('users.enabled')}</option>
                <option value="inactive">{t('users.disabled')}</option>
              </select>
            ) : (
              <span><span className={`status-badge ${user.enabled ? 'active' : 'inactive'}`}>{user.enabled ? t('users.enabled') : t('users.disabled')}</span></span>
            )}
          </div>
          <div className="cu-field">
            <label htmlFor="ud-verified">{t('users.emailVerified')}</label>
            {isEditing ? (
              <select id="ud-verified" className="cu-input" data-testid="field-user-email-verified-edit" value={state.editEmailVerified ? 'active' : 'inactive'}
                      onChange={(e) => state.setEditEmailVerified(e.target.value === 'active')} disabled={isLoading}>
                <option value="active">{t('common.yes')}</option>
                <option value="inactive">{t('common.no')}</option>
              </select>
            ) : (
              <span><span className={`status-badge ${user.emailVerified ? 'active' : 'neutral'}`}>{user.emailVerified ? t('common.yes') : t('common.no')}</span></span>
            )}
          </div>
          <div className="cu-field">
            <label>{t('users.created')}</label>
            <span className="ud-value">{user.createdDate ? formatDateTime(user.createdDate) : '–'}</span>
          </div>
        </div>
      </section>
    </div>
  );
};
